import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, LogOut, Bookmark, FileText, MapPin, Clock, Trash2,
  ArrowRight, ArrowLeft, Building2, DollarSign, Briefcase, TrendingUp,
} from "lucide-react";
import DcPage from "@/components/landing/dcv2/DcPage";
import AvatarInitials from "@/components/ui/avatar-initials";
import { useAuth } from "@/app/hooks/useAuth";
import { useToast } from "@/app/hooks/use-toast";
import { formatDate } from "@/utils/formatters/date.formatters";
import { glassCardStyle, surfaceStyle } from "@/components/auth/authStyles";
import { useSavedJobs } from "@/app/hooks/useSavedJobs";
import { candidateApi } from "@/services/endpoints/candidate";
import { employmentLabel, workplaceLabel, seniorityLabel } from "@/components/jobs/jobCardUtils";
import type { CandidateApplicationItem } from "@/services/api/types";

const EASE = [0.16, 1, 0.3, 1] as const;

const fmt = (iso: string | null) => (iso ? formatDate(iso, iso, "MMM d, yyyy") : "—");

/** Humanize a raw status enum ("interview_scheduled" -> "Interview Scheduled"). */
const humanizeStatus = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim() || "—";

/** Maps a status string to an electric tone. */
function statusTone(status: string): React.CSSProperties {
  const s = status.toLowerCase();
  if (s.includes("reject") || s.includes("declin")) {
    return { background: "hsl(var(--destructive) / 0.12)", color: "hsl(var(--destructive))", border: "1px solid hsl(var(--destructive) / 0.25)" };
  }
  if (s.includes("accept") || s.includes("hire") || s.includes("offer")) {
    return { background: "rgba(var(--pos-rgb),0.1)", color: "var(--pos)", border: "1px solid rgba(var(--pos-rgb),0.25)" };
  }
  if (s.includes("interview")) {
    return { background: "rgba(var(--acc2-rgb),0.1)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.25)" };
  }
  return { background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.2)" };
}

function Pill({ children, tone = "surface" }: { children: React.ReactNode; tone?: "surface" | "cyan" | "purple" }) {
  const tones: Record<string, React.CSSProperties> = {
    surface: { background: "var(--landing-surface)", color: "var(--landing-text-secondary)", border: "1px solid var(--landing-glass-border)" },
    cyan: { background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.18)" },
    purple: { background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.18)" },
  };
  return (
    <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full" style={tones[tone]}>
      {children}
    </span>
  );
}

/** Compact metric tile shown in the identity sidebar. */
function StatTile({ icon: Icon, label, value, accent }: { icon: React.ElementType; label: string; value: number; accent: string }) {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
    >
      <div className="flex items-center gap-1.5 mb-1.5" style={{ color: accent }}>
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold leading-none" style={{ color: "var(--landing-text)" }}>{value}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="rounded-2xl p-12 text-center" style={glassCardStyle}>
      <div
        className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
      >
        <Icon className="h-8 w-8 text-[var(--acc2)]/60" />
      </div>
      <p className="font-medium mb-1" style={{ color: "var(--landing-text)" }}>{title}</p>
      <p className="text-sm mb-6" style={{ color: "var(--landing-text-muted)" }}>{body}</p>
      <Link to="/jobs" className="btn-electric px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2">
        Browse open roles
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="rounded-2xl p-12 text-center" style={glassCardStyle}>
      <p className="text-sm" style={{ color: "var(--landing-text-muted)" }}>{label}</p>
    </div>
  );
}

export default function Profile() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { savedJobs: saved, removeSaved, loading: savedLoading } = useSavedJobs();

  const [tab, setTab] = useState<"saved" | "applications">("saved");
  const [applications, setApplications] = useState<CandidateApplicationItem[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      navigate(`/auth?redirect=${encodeURIComponent("/profile")}`, { replace: true });
    }
  }, [isAuthenticated, user?.id, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    setAppsLoading(true);
    candidateApi
      .getApplications()
      .then((data) => {
        if (active) setApplications(data.items);
      })
      .catch(() => {
        if (active) setApplications([]);
      })
      .finally(() => {
        if (active) setAppsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);

  if (!isAuthenticated || !user?.id) return null;

  const handleRemoveSaved = async (jobPostingId: string, title: string) => {
    try {
      await removeSaved(jobPostingId);
      toast({ title: "Removed", description: `"${title}" was removed from your saved jobs.` });
    } catch {
      toast({ title: "Couldn't remove", description: "Please try again in a moment.", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    logout();
    toast({ title: "Signed out", description: "You've been successfully signed out." });
    navigate("/auth");
  };


  return (
    <DcPage>
      <section className="relative min-h-[calc(100vh-80px)] overflow-hidden pt-24 md:pt-28 pb-20" style={{ background: "var(--landing-bg)" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(var(--landing-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 70% 50% at 50% 10%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 10%, black, transparent)",
          }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(var(--acc2-rgb),0.12) 0%, rgba(var(--acc2-rgb),0.06) 45%, transparent 70%)",
            filter: "blur(80px)",
            opacity: "var(--landing-glow-mult)",
          }}
        />

        <div className="container relative z-10">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 lg:grid-cols-[320px_1fr] lg:gap-8">
              {/* ─── Identity sidebar ─── */}
              <motion.aside
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="lg:sticky lg:top-24 lg:self-start"
              >
                <div className="rounded-2xl overflow-hidden" style={glassCardStyle}>
                  {/* gradient banner */}
                  <div
                    className="h-20"
                    style={{ background: "linear-gradient(135deg, rgba(var(--acc2-rgb),0.28), rgba(var(--pos-rgb),0.16), rgba(var(--acc2-rgb),0.24))" }}
                  />
                  <div className="px-5 pb-5">
                    {/* avatar overlaps banner */}
                    {/* Shared with the account shell. The shape is kept —
                        `rounded-2xl` overrides the component's circle through
                        tailwind-merge, because this one overlaps the banner and
                        a disc would read as a different element.
                        The colours change source but not value: `var(--acc2)` and
                        `border rgba(var(--acc2-rgb),.3)` were literals of exactly
                        `--brand-cyan` (190 100% 50%), so this now follows the
                        token instead of pinning it. The cyan-to-violet gradient
                        goes, and it is the only thing that does — it appeared
                        nowhere else in the app. */}
                    <AvatarInitials
                      firstName={user.firstName}
                      lastName={user.lastName}
                      size="lg"
                      className="-mt-10 mb-3 rounded-2xl ring-4 ring-[color:var(--landing-bg)]"
                    />

                    <h1 className="text-lg font-bold tracking-tight leading-snug" style={{ color: "var(--landing-text)" }}>
                      {user.firstName} {user.lastName}
                    </h1>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs truncate" style={{ color: "var(--landing-text-muted)" }}>
                      <User className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </p>
                    <span
                      className="mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: "rgba(var(--acc2-rgb),0.08)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.2)" }}
                    >
                      Candidate
                    </span>

                    {/* stats */}
                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                      <StatTile icon={Bookmark} label="Saved" value={saved.length} accent="var(--acc2)" />
                      <StatTile icon={FileText} label="Applied" value={applications.length} accent="var(--acc2)" />
                    </div>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
                      style={surfaceStyle}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>

                <Link
                  to="/jobs"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium transition-colors hover:text-[var(--acc2)]"
                  style={{ color: "var(--landing-text-muted)" }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to jobs
                </Link>
              </motion.aside>

              {/* ─── Main content ─── */}
              <motion.main
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
                className="min-w-0"
              >
                {/* Tabs */}
                <div
                  className="relative mb-6 grid grid-cols-2 gap-1 rounded-xl p-1"
                  style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)", maxWidth: "26rem" }}
                >
                  <div
                    className="absolute top-1 h-[calc(100%-8px)] rounded-lg transition-all duration-300 ease-expo-out"
                    style={{
                      left: tab === "saved" ? "4px" : "calc(50% + 2px)",
                      width: "calc(50% - 6px)",
                      background: "var(--acc2)",
                      boxShadow: "0 4px 16px rgba(var(--acc2-rgb),0.3)",
                    }}
                  />
                  {([
                    { key: "saved", label: "Saved jobs", count: saved.length, icon: Bookmark },
                    { key: "applications", label: "Applications", count: applications.length, icon: FileText },
                  ] as const).map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className="relative z-10 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors"
                      style={{ color: tab === t.key ? "var(--btnfg)" : "var(--landing-text-muted)" }}
                    >
                      <t.icon className="h-4 w-4" />
                      {t.label}
                      <span
                        className="ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                        style={
                          tab === t.key
                            ? { background: "rgba(var(--ink-rgb),0.18)", color: "var(--btnfg)" }
                            : { background: "var(--landing-glass-bg)", color: "var(--landing-text-muted)", border: "1px solid var(--landing-glass-border)" }
                        }
                      >
                        {t.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Content */}
                {tab === "saved" ? (
                  savedLoading && saved.length === 0 ? (
                    <LoadingState label="Loading your saved jobs…" />
                  ) : saved.length === 0 ? (
                    <EmptyState
                      icon={Bookmark}
                      title="No saved jobs yet"
                      body="Save roles you're interested in and they'll show up here."
                    />
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {saved.map((job, i) => {
                        const closed = job.status === "closed";
                        return (
                          <motion.div
                            key={job.jobPostingId}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, delay: i * 0.05, ease: EASE }}
                            className="group relative rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5"
                            style={glassCardStyle}
                          >
                            <button
                              type="button"
                              onClick={() => handleRemoveSaved(job.jobPostingId, job.title)}
                              className="absolute right-3 top-3 p-1.5 rounded-lg transition-colors hover:text-destructive"
                              style={{ color: "var(--landing-text-dim)" }}
                              aria-label={`Remove ${job.title} from saved jobs`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <Link to={`/job/${job.jobPostingId}`} className="block pr-8">
                              <div className="flex items-center gap-2 mb-1">
                                <h3
                                  className="text-base font-semibold leading-snug tracking-tight transition-colors group-hover:text-[var(--acc2)]"
                                  style={{ color: closed ? "var(--landing-text-muted)" : "var(--landing-text)" }}
                                >
                                  {job.title}
                                </h3>
                                {closed && (
                                  <span
                                    className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full shrink-0"
                                    style={{ background: "rgba(var(--acc2-rgb),0.1)", color: "var(--acc2)", border: "1px solid rgba(var(--acc2-rgb),0.25)" }}
                                  >
                                    Unavailable
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mb-3" style={{ color: "var(--landing-text-muted)" }}>
                                {job.location && (
                                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>
                                )}
                                {job.employmentType && (
                                  <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{employmentLabel(job.employmentType)}</span>
                                )}
                                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Saved {fmt(job.savedAt)}</span>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                {job.workplaceType && <Pill tone="cyan">{workplaceLabel(job.workplaceType)}</Pill>}
                                {job.seniorityLevel && <Pill tone="purple">{seniorityLabel(job.seniorityLevel)}</Pill>}
                                {job.salaryRange && (
                                  <span
                                    className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full inline-flex items-center gap-1"
                                    style={{ background: "rgba(var(--pos-rgb),0.08)", color: "var(--pos)", border: "1px solid rgba(var(--pos-rgb),0.22)" }}
                                  >
                                    <DollarSign className="h-2.5 w-2.5" />{job.salaryRange}
                                  </span>
                                )}
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  )
                ) : appsLoading && applications.length === 0 ? (
                  <LoadingState label="Loading your applications…" />
                ) : applications.length === 0 ? (
                  <EmptyState
                    icon={FileText}
                    title="No applications yet"
                    body="When you apply to a role, it'll appear here so you can track its status."
                  />
                ) : (
                  <div className="space-y-3">
                    {applications.map((app, i) => (
                      <motion.div
                        key={app.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: i * 0.05, ease: EASE }}
                      >
                        <Link
                          to={`/job/${app.jobPostingId}`}
                          className="group flex items-center gap-4 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5"
                          style={glassCardStyle}
                        >
                          <div
                            className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                            style={{ background: "rgba(var(--acc2-rgb),0.08)", border: "1px solid rgba(var(--acc2-rgb),0.18)" }}
                          >
                            <Briefcase className="h-5 w-5 text-[var(--acc2)]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold leading-snug tracking-tight truncate transition-colors group-hover:text-[var(--acc2)]" style={{ color: "var(--landing-text)" }}>
                              {app.jobTitle}
                            </h3>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-0.5" style={{ color: "var(--landing-text-muted)" }}>
                              {app.organizationName && (
                                <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{app.organizationName}</span>
                              )}
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Applied {fmt(app.appliedAt)}</span>
                              {app.rating != null && (
                                <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" />Match {app.rating}/5</span>
                              )}
                            </div>
                          </div>
                          <span className="rounded-full px-3 py-1 text-[11px] font-semibold shrink-0" style={statusTone(app.status)}>
                            {humanizeStatus(app.status)}
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.main>
            </div>
          </div>
        </div>
      </section>
    </DcPage>
  );
}
